import React, { useContext } from "react";
/**
 * Purpose: provide seamless transition between 2 content items
 */
import { FunctionComponent } from "react";
import { GqlZoneItemTransition } from "../../../../../store/graphqlTypes";
import { TimelineSlotTransition } from "../TimelineSlotTransition/TimelineSlotTransition";
import {
  e2eTestSlotIdentifier,
  useIsPreloadItem,
  useShouldRenderForPreloading,
  useTimelineContentVisible,
} from "../utils";
import classnames from "classnames";
import styles from "./TimelineSlot.module.css";
import { timelineSlotPrefix } from "../../../../../test-utils/testIds";
import { isCrossTransition } from "../TimelineSlotTransition/transitionUtils";
import ClientApiContext from "../../../../../ClientApiContext";
export interface TimelineSlotProps {
  index: number;
  transition: GqlZoneItemTransition | undefined;
  isActiveItemTransitioningOut: boolean;
  forceBackground?: boolean;
  forceVisible?: boolean;
  targetTimelineId: string | undefined;
  indexInTimeline: number | undefined;
}

/**
 * Is designed to display content and make transitions between items.
 */
export const TimelineSlot: FunctionComponent<TimelineSlotProps> = (props) => {
  const {
    children,
    transition,
    index,
    isActiveItemTransitioningOut,
    forceBackground,
    targetTimelineId,
    indexInTimeline,
    forceVisible,
  } = props;
  const clientApiContext = useContext(ClientApiContext);
  const isTizenPlayer = clientApiContext?.playbackConfig?.isTizenPlayer;
  const isPreload = useIsPreloadItem(targetTimelineId, indexInTimeline);

  const isCross = transition
    ? isCrossTransition(transition.type, transition.direction)
    : false;

  const isContentVisible = useTimelineContentVisible(
    isPreload,
    isCross,
    isActiveItemTransitioningOut
  );

  const shouldRenderForPreloading = useShouldRenderForPreloading(
    targetTimelineId
  );

  /**
   * Only Tizen need to specific preload css since the style visibility doesn't work properly on tizen
   * it need to use scale to preventing the overlapping video
   */
  const tizenVideoClass = {
    [styles.activeViewer]: isContentVisible && isTizenPlayer,
    [styles.tizenVideoPreload]: !isContentVisible && isTizenPlayer,
  };
  const contentClassnames = classnames(styles.base, {
    [styles.visible]: isContentVisible,
    ...tizenVideoClass,
  });

  return (
    <div className={styles.slotContainer}>
      <TimelineSlotTransition
        transition={transition}
        isPreload={isPreload}
        isActiveItemTransitioningOut={isActiveItemTransitioningOut}
        forceBackground={forceBackground}
      >
        <div
          className={contentClassnames}
          id={e2eTestSlotIdentifier(index)}
          aria-label={e2eTestSlotIdentifier(index)}
          data-testid={`${timelineSlotPrefix}${index}`}
        >
          {shouldRenderForPreloading || isContentVisible || forceVisible
            ? children
            : null}
        </div>
      </TimelineSlotTransition>
    </div>
  );
};
